import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import ConfirmIcon from '@material-ui/icons/Check';
import CancelIcon from '@material-ui/icons/Close';
import useApiFetch, {apiEndpoint} from '@tracker/shared/src/Auth/Api';
import {usePreferencesState} from '@tracker/shared/src/Preferences/PreferencesProvider';
import {debounce} from 'lodash';
import {Autocomplete} from 'mui-rff';
import {useSnackbar} from 'notistack';
import React, {Ref, useEffect, useMemo, useState} from 'react';
import {useForm, useFormState} from 'react-final-form'
import {Task} from '../../pages/TimeEntries';
import {useProjects} from "./MyProjectsProvider";
import {useRecentlyUsedTasks} from './RecentlyUsedTasksProvider';

type Props = {
    className ?: string;
    required ?: boolean;
    label : string;
    name : string;
    taskHierarchy : boolean;
    setNoteFocus : () => void;
    taskRef ?: Ref<HTMLDivElement | null>
};

const collator = new Intl.Collator('en-US', {sensitivity: 'base'});

const sortTasks = (a : Task, b : Task) => {
    const isFavoriteCompare = (a.favoriteName !== undefined ? -1 : 1) - (b.favoriteName !== undefined ? -1 : 1);

    if (isFavoriteCompare !== 0) {
        return isFavoriteCompare;
    }

    if (a.favoriteName !== undefined && b.favoriteName !== undefined) {
        const favoriteCompare = collator.compare(a.favoriteName, b.favoriteName);

        if (favoriteCompare !== 0) {
            return favoriteCompare;
        }
    }

    return collator.compare(a.name, b.name);
};

const formatTask = (task : Task, showTaskId : boolean) => [
    task.project,
    task.component,
    task.name,
    task.id
].join(': ');

const TaskSelect : React.FC<Props> = ({className, label, name, taskHierarchy, setNoteFocus, taskRef} : Props) => {
    const apiFetch = useApiFetch();
    const recentlyUsedTasks = useRecentlyUsedTasks();
    const [searchedTasks, setSearchedTasks] = useState<Task[]>([]);
    const [loading, setLoading] = useState(false);
    const {preferences} = usePreferencesState();
    const {enqueueSnackbar} = useSnackbar();
    const [creatingTask, setCreatingTask] = useState<boolean>(false);
    const formState = useFormState();
    const form = useForm();
    const {myProjects} = useProjects();

    const createTask = React.useCallback(async (fakeTask : Task) => {
        setCreatingTask(true);
        const url = new URL('/v1/tasks/', apiEndpoint);
        try {
            const result = await apiFetch(url.href, {
                method: 'POST',
                body: JSON.stringify({
                    name: fakeTask.name,
                    componentId: fakeTask.componentId,
                    projectId: fakeTask.projectId,
                }),
            });

            if (!result.ok) {
                let extendedMessage = '';
                try {
                    const response = await result.json();
                    if ('message' in response && typeof response.message === 'string') {
                        extendedMessage = ' ' + response.message;
                    }
                    // eslint-disable-next-line no-empty
                } catch (e) {}
                enqueueSnackbar(`Could not create task.${extendedMessage}`, {variant: 'error'});
                setCreatingTask(false);
                return;
            }

            const task = await result.json();

            setSearchedTasks([...searchedTasks, task]);
            form.change('task', task);
            setCreatingTask(false);
        } catch (e) {
            enqueueSnackbar('Could not create task.', {variant: 'error'});
            setCreatingTask(false);
        }
    }, [apiFetch, enqueueSnackbar, form, searchedTasks]);

    const options : Task[] = useMemo(() => {
        const mappedTasks = new Map(recentlyUsedTasks.map(task => [task.id, true]));
        const selectedArray = formState.values.task ? [formState.values.task] : [];

        return [
            ...[...recentlyUsedTasks].sort(sortTasks),
            ...searchedTasks.filter(task => !mappedTasks.has(task.id)).sort(sortTasks),
            ...selectedArray.filter(task => !mappedTasks.has(task.id))
                .filter(task => !searchedTasks.find((t) => t.id === task.id))
        ];
    }, [formState.values.task, recentlyUsedTasks, searchedTasks]);

    useEffect(() => {
        if (formState.values.task?.id === -1) {
            form.change('task', null);
        }
    }, [form, formState.values.task])

    const {search, abort} = useMemo(() => {
        let aborted = false;

        const search = debounce(
            async (value : string) => {
                if (aborted) {
                    return aborted = false;
                }

                if (value === '' && !formState.values.project) {
                    return setSearchedTasks([]);
                }

                setLoading(true);
                const url = new URL('/v1/tasks/personal', apiEndpoint);
                url.searchParams.set('search', value);

                if (taskHierarchy) {
                    formState.values.project?.id && url.searchParams.set('projectId', String(formState.values.project.id));
                    formState.values.component?.id && url.searchParams.set('componentId', String(formState.values.component.id));
                }
                const result = await apiFetch(url.href);

                if (aborted) {
                    return aborted = false;
                }

                setLoading(false);

                if (!result.ok) {
                    return setSearchedTasks([]);
                }

                const data = await result.json();

                if (aborted) {
                    return aborted = false;
                }

                setSearchedTasks(data);
            },
            250
        );

        return {
            search,
            abort: () => {
                aborted = true;
                search.cancel();
            },
        };
    }, [formState.values, taskHierarchy, apiFetch]);

    useEffect(() => {
        search('');
    }, [formState.values.project, formState.values.component, search]);

    useEffect(() => abort, [abort]);

    return (
        <Autocomplete
            openOnFocus
            textFieldProps={{
                variant: 'outlined',
                inputRef: taskRef
            }}
            className={className}
            name={name}
            label={label}
            loading={loading}
            noOptionsText="No tasks"
            onInputChange={(event, value) => search(value)}
            getOptionLabel={option => !option ? '' : formatTask(option, preferences.timeEntry.showTaskId)}
            options={options}
            renderOption={option => (
                <div style={{width: '100%'}}>
                    {option.favoriteName !== undefined && (
                        <div style={{color: '#4caf50'}}>{option.favoriteName}</div>
                    )}
                    <div style={{float: 'right'}}>{option.project}</div>
                    <div style={{fontWeight: 'bold'}}>{option.component}</div>
                    <div>
                        {option.id === -1
                            ?  <>
                                <Tooltip title="Create Task">
                                    <IconButton disabled={creatingTask} color="primary" onClick={async () => !creatingTask && await createTask(option)}><ConfirmIcon/></IconButton>
                                </Tooltip>
                                <Tooltip title="Don't Create Task">
                                    <IconButton disabled={creatingTask}><CancelIcon/></IconButton>
                                </Tooltip>
                            </>: ''}
                        {option.id === -1 ? 'Create Task "' : ''}
                        {option.name}
                        {option.id === -1 ? '"' : ''}
                    </div>
                </div>
            )}
            filterOptions={(options, state) => {
                const words = state.inputValue.toLocaleLowerCase().split(' ');
                let exactMatch = false;

                const filteredTasks = options.filter(option => {
                    if (taskHierarchy) {
                        if (formState.values.component?.id && option.componentId !== formState.values.component.id) {
                            return false;
                        }
                        if (formState.values.project?.id && option.projectId !== formState.values.project.id) {
                            return false;
                        }
                    }

                    const formattedTask = formatTask(option, preferences.timeEntry.showTaskId);

                    if (option.name === state.inputValue || formattedTask === state.inputValue) {
                        exactMatch = true;
                    }

                    const contents = [
                        formattedTask,
                        option.favoriteName,
                        option.project,
                        option.component,
                        option.name,
                        option.id,
                    ].join(' ').toLocaleLowerCase();

                    for (const word of words) {
                        if (!contents.includes(word)) {
                            return false;
                        }
                    }

                    return true;
                });

                if (
                    !exactMatch
                    && state.inputValue.length > 1
                    && taskHierarchy
                    && formState.values.component
                    && formState.values.project
                ) {
                    filteredTasks.push({
                        id: -1,
                        name: state.inputValue,
                        component: formState.values.component.name,
                        componentId: formState.values.component.id,
                        project: formState.values.project.name,
                        projectId: formState.values.project.id,
                        billable: true,
                    });
                }
                return filteredTasks;
            }}
            getOptionSelected={(option, value) => option.id === value.id}
            onChange={(_event, value) => {
                if (value && value !== '-1') {
                    let isPMTimeEntry = 0;
                    if (!taskHierarchy) {
                        if (value && !Array.isArray(value) && typeof value !== 'string') {
                            const project = myProjects
                                .find(p => p.id === value.projectId)
                            isPMTimeEntry = project?.pmFixedBilling === true && project.isPmRole ? 1 : 0;
                        }
                        form.change('isPMTimeEntry', isPMTimeEntry);
                    }

                    setNoteFocus();
                }
            }}
        />
    );
};

export default TaskSelect;
